// src/cockpit4/components/AIQuestionMinerPanel.tsx
// ============================================
// AI 问题挖掘（GEO+DSO 新工具）
//
// 功能：输入行业 / 品类 / 品牌 / 关键词 → AI 挖掘高热度问题
//   - 行业/主题
//   - 品牌/产品
//   - 目标人群
//   - 关键词
//   - 问题类型勾选（产品选型 / 行业定义 / 落地案例 / 原理关系 / 对比评测 / 价格咨询 / 使用教程 / 售后服务）
//   - 挖掘模型（行业热度 / 用户意图 / 长尾扩展）
//   - 输出：AI 问题推荐列表（问题 / 类型 / 热度值 / 上升趋势 / 收藏 / 一键加入监测 / 一键追问）
// ============================================

import { useState } from "react";
import { motion } from "framer-motion";
import {
  Sparkles, Zap, Search, Bookmark, BookmarkCheck, PlayCircle,
  TrendingUp, ChevronDown, Loader2, RefreshCw, Lightbulb, Tags, Users, Layers,
} from "lucide-react";
import EmptyState, { SectionHeader } from "./_MonitorEmptyState";

type QType = "产品选型" | "行业定义" | "落地案例" | "原理关系" | "对比评测" | "价格咨询" | "使用教程" | "售后服务";
type ModelMode = "行业热度" | "用户意图" | "长尾扩展";

const QUESTION_TYPES: QType[] = ["产品选型","行业定义","落地案例","原理关系","对比评测","价格咨询","使用教程","售后服务"];
const TYPE_COLOR: Record<QType, string> = {
  产品选型: "bg-violet-500/10 border border-violet-400/40 text-violet-300",
  行业定义: "bg-cyan-500/10 border border-cyan-400/40 text-cyan-300",
  落地案例: "bg-emerald-500/10 border border-emerald-400/40 text-emerald-300",
  原理关系: "bg-amber-500/10 border border-amber-400/40 text-amber-300",
  对比评测: "bg-fuchsia-500/10 border border-fuchsia-400/40 text-fuchsia-300",
  价格咨询: "bg-rose-500/10 border border-rose-400/40 text-rose-300",
  使用教程: "bg-indigo-500/10 border border-indigo-400/40 text-indigo-300",
  售后服务: "bg-slate-500/10 border border-slate-400/40 text-slate-300",
};

interface MinedQ {
  id: string;
  question: string;
  type: QType;
  heat: number;
  trend: number; // %
  saved: boolean;
  monitored: boolean;
}

const Chips = <T extends string>({ items, value, onChange, colorBy }:{
  items: readonly T[]; value: T[]; onChange: (v: T[]) => void; colorBy?: Record<T, string>;
}) => (
  <div className="flex flex-wrap gap-2">
    {items.map(t => {
      const active = value.includes(t);
      const base = colorBy?.[t];
      return (
        <button key={t} onClick={() => onChange(active ? value.filter(x=>x!==t) : [...value, t])}
          className={`px-3 py-1.5 rounded-full text-[12px] border transition-all ${
            active ? (base || "bg-violet-500/20 border border-violet-400/50 text-violet-200") : "bg-white/[0.03] border border-white/[0.08] text-gray-400 hover:text-white hover:border-white/20"
          }`}>
          {t}
        </button>
      );
    })}
  </div>
);

export default function AIQuestionMinerPanel({
  onAddToMonitor, onOpenMonitorPlan,
}:{
  onAddToMonitor?: () => void;
  onOpenMonitorPlan?: () => void;
}) {
  const [industry, setIndustry] = useState("企业AI操作系统");
  const [brand, setBrand] = useState("小舍科技 · XEAOS");
  const [target, setTarget] = useState("企业主 / CIO / 运营负责人");
  const [keywords, setKeywords] = useState("GEO优化, DSO增长, AI引用, 品牌官网");
  const [types, setTypes] = useState<QType[]>(["产品选型","对比评测","落地案例"]);
  const [model, setModel] = useState<ModelMode>("行业热度");
  const [running, setRunning] = useState(false);
  const [list, setList] = useState<MinedQ[]>([]);
  const [emptyMsg, setEmptyMsg] = useState("");

  // 调用后端真实 DeepSeek 问题挖掘（无模拟数据/硬编码）
  const run = async () => {
    if (running) return;
    setRunning(true);
    setEmptyMsg("");
    try {
      const r = await fetch("/geo-question-mine", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          industry, brand, target, keywords,
          types: types.length ? types : QUESTION_TYPES,
          model,
        }),
      });
      const d = await r.json();
      if (d?.success && Array.isArray(d.questions)) {
        setList(d.questions as MinedQ[]);
        if (d.empty) setEmptyMsg(d.msg || "未返回有效问题，请调整输入后重试");
      } else {
        setList([]);
        setEmptyMsg(d?.error || "挖掘失败，请稍后重试");
      }
    } catch {
      setList([]);
      setEmptyMsg("无法连接服务器，请确认已登录并刷新后重试");
    } finally {
      setRunning(false);
    }
  };

  const toggleSave = (id:string) => setList(list.map(q => q.id===id ? {...q, saved:!q.saved} : q));
  const toggleMonitor = (id:string) => setList(list.map(q => q.id===id ? {...q, monitored:!q.monitored} : q));

  const saveAllChecked = list.filter(q => q.saved).length;
  const monAllChecked = list.filter(q => q.monitored).length;

  return (
    <div className="px-4 py-5">
      {/* 顶部：输入区 */}
      <motion.div
        initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }}
        className="rounded-2xl bg-white/[0.03] border border-white/[0.08] p-5 mb-5">
        <div className="flex items-start justify-between mb-4 gap-4">
          <div>
            <div className="flex items-center gap-2 text-[16px] font-bold text-white">
              <Sparkles size={18} className="text-cyan-400" />
              AI 问题挖掘
              <span className="text-[11px] font-normal text-gray-500 ml-1">挖掘行业高热度 AI 用户问题，一键加入品牌监测</span>
            </div>
            <div className="text-[12px] text-gray-500 mt-1">输入你的行业、品牌、目标人群与关键词，AI 会自动识别用户真实搜索问题与热度趋势</div>
          </div>
          <button onClick={onOpenMonitorPlan}
            className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] bg-violet-500/15 border border-violet-400/40 text-violet-200 hover:bg-violet-500/25 transition-all">
            <PlayCircle size={14} /> 查看监测计划
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field Icon={<Layers size={14} />} label="行业 / 主题" placeholder="如：AI企业系统 / 数字营销SaaS" value={industry} onChange={setIndustry} />
          <Field Icon={<Tags size={14} />} label="品牌 / 产品" placeholder="如：小舍科技 XEAOS" value={brand} onChange={setBrand} />
          <Field Icon={<Users size={14} />} label="目标人群" placeholder="如：企业主 / CIO / 新媒体运营" value={target} onChange={setTarget} />
          <Field Icon={<Lightbulb size={14} />} label="核心关键词（英文逗号分隔）" placeholder="如：GEO, DSO, AI搜索, 品牌监测" value={keywords} onChange={setKeywords} />
        </div>

        <div className="mt-5">
          <div className="text-[12px] text-gray-400 mb-2 flex items-center gap-1.5">
            <Zap size={12} className="text-amber-400" /> 要挖掘的问题类型（可多选，不选则全类型）
          </div>
          <Chips<QType> items={QUESTION_TYPES} value={types} onChange={setTypes} colorBy={TYPE_COLOR} />
        </div>

        <div className="mt-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="text-[12px] text-gray-400 mr-1">挖掘模型：</div>
            {(["行业热度","用户意图","长尾扩展"] as ModelMode[]).map(m => (
              <button key={m} onClick={()=>setModel(m)}
                className={`px-3 py-1.5 rounded-full text-[12px] border transition-all ${
                  model===m ? "bg-gradient-to-r from-violet-500 to-cyan-500 text-white border-transparent shadow-lg shadow-violet-500/20"
                            : "bg-white/[0.03] border border-white/[0.08] text-gray-400 hover:text-white hover:border-white/20"
                }`}>{m}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={run}
              className="flex items-center gap-1.5 px-5 py-2 rounded-md text-[13px] font-semibold bg-gradient-to-r from-violet-500 to-cyan-500 text-white hover:opacity-95 shadow-lg shadow-violet-500/20 transition-all">
              {running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {running ? "正在挖掘..." : "开始挖掘AI问题"}
            </button>
            <button onClick={run} className="flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] bg-white/[0.04] border border-white/[0.08] text-gray-400 hover:text-white hover:border-white/20">
              <RefreshCw size={13} /> 换一批
            </button>
          </div>
        </div>
      </motion.div>

      {/* 结果区 */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/[0.08] overflow-hidden">
        <div className="px-5 py-3 flex items-center justify-between border-b border-white/[0.06] flex-wrap gap-2">
          <SectionHeader label={`推荐问题 · 共 ${list.length} 条`}>
            <div className="flex items-center gap-2 text-[12px]">
              <span className="text-gray-500">已收藏：<span className="text-violet-400 font-semibold">{saveAllChecked}</span></span>
              <span className="text-gray-500">已监测：<span className="text-cyan-400 font-semibold">{monAllChecked}</span></span>
            </div>
          </SectionHeader>
        </div>

        <div className="px-3 py-3 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[280px]">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input placeholder="搜索问题内容..." className="w-full pl-9 pr-3 py-2 rounded-md bg-white/[0.04] border border-white/[0.08] text-[12.5px] text-gray-200 placeholder:text-gray-600 outline-none focus:border-violet-400/50" />
          </div>
          <button className="px-3 py-2 rounded-md bg-white/[0.04] border border-white/[0.08] text-[12px] text-gray-400 hover:text-white hover:border-white/20 flex items-center gap-1.5">
            <Bookmark size={12} /> 仅显示收藏 <ChevronDown size={10} />
          </button>
          <button onClick={onAddToMonitor} className="px-4 py-2 rounded-md text-[12.5px] bg-gradient-to-r from-violet-500 to-cyan-500 text-white font-semibold flex items-center gap-1.5 hover:opacity-95">
            <PlayCircle size={13} /> 一键加入监测
          </button>
        </div>

        <div className="overflow-x-auto">
          {list.length === 0 ? (
            <EmptyState text={emptyMsg || "暂无AI问题，点击「开始挖掘AI问题」试试"} />
          ) : (
            <table className="w-full text-[12.5px] border-collapse">
              <thead>
                <tr className="bg-white/[0.04] text-gray-400 text-[11.5px]">
                  <th className="text-left p-3 rounded-l-lg"><span className="inline-flex items-center gap-1">AI问题 <TrendingUp size={10} className="inline text-cyan-400" /></span></th>
                  <th className="text-left p-3">问题类型</th>
                  <th className="text-left p-3"><span className="inline-flex items-center gap-1">热度值 <ChevronDown size={10} /></span></th>
                  <th className="text-left p-3">上升趋势</th>
                  <th className="text-left p-3 rounded-r-lg w-[200px]">操作</th>
                </tr>
              </thead>
              <tbody>
                {list.map((q,i) => (
                  <motion.tr key={q.id}
                    initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i*0.03 }}
                    className="border-b border-white/[0.05] hover:bg-white/[0.02]">
                    <td className="p-3 pr-6 text-white">{q.question}</td>
                    <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-[11px] border ${TYPE_COLOR[q.type]}`}>{q.type}</span></td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-violet-500 to-cyan-500" style={{ width: `${Math.min(100, q.heat/10)}%` }} />
                        </div>
                        <span className="font-mono text-gray-200 text-[12px]">{q.heat}</span>
                      </div>
                    </td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 text-[12px] font-semibold ${q.trend>=0 ? "text-emerald-400" : "text-rose-400"}`}>
                        <TrendingUp size={12} className={q.trend<0 ? "rotate-180" : ""} />
                        {q.trend>=0 ? "+" : ""}{q.trend}%
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <button onClick={()=>toggleSave(q.id)}
                          className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11.5px] border transition-all ${
                            q.saved ? "bg-amber-500/15 border border-amber-400/40 text-amber-300" : "bg-white/[0.03] border border-white/[0.08] text-gray-400 hover:text-amber-300 hover:border-amber-400/40"
                          }`}>
                          {q.saved ? <BookmarkCheck size={11} /> : <Bookmark size={11} />}
                          {q.saved ? "已收藏" : "收藏"}
                        </button>
                        <button onClick={()=>{toggleMonitor(q.id); onAddToMonitor?.();}}
                          className={`flex items-center gap-1 px-2.5 py-1 rounded text-[11.5px] border transition-all ${
                            q.monitored ? "bg-cyan-500/15 border border-cyan-400/40 text-cyan-300" : "bg-white/[0.03] border border-white/[0.08] text-gray-400 hover:text-cyan-300 hover:border-cyan-400/40"
                          }`}>
                          <PlayCircle size={11} />
                          {q.monitored ? "监测中" : "加入监测"}
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({Icon, label, placeholder, value, onChange}:{
  Icon: React.ReactNode; label: string; placeholder: string; value: string; onChange: (s: string) => void;
}) {
  return (
    <label className="block">
      <div className="text-[12px] text-gray-400 mb-1.5 flex items-center gap-1.5">{Icon} {label}</div>
      <input value={value} placeholder={placeholder} onChange={e=>onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-md bg-white/[0.04] border border-white/[0.08] text-[13px] text-gray-200 placeholder:text-gray-600 outline-none focus:border-violet-400/50 transition-colors" />
    </label>
  );
}
